import { showError } from '@components/app-error';
import { UserProfileIcon } from '@components/avatars';
import { Button } from '@components/buttons';
import { Dropdown } from '@components/dropdown';
import { IcoCalendar, IcoList, IcoX } from '@components/icons';
import { Breadcrumbs, Crumb } from '@course/components/breadcrumbs';
import { useConfiguration, useCurrentUser } from 'client/lib/auth';
import { rpx } from 'client/lib/rpx-client';
import { useDocumentTitle } from 'client/utils/use-document-title';
import { useEsc } from 'client/utils/use-esc';
import { useLocalStorageState } from 'client/lib/hooks/use-localstorage-state';
import { ComponentChildren, createContext } from 'preact';
import { useState, useContext } from 'preact/hooks';

const SidebarContext = createContext<{ isCollapsed: boolean }>({ isCollapsed: false });

type RootPageProps = {
  title: string;
  logo: ComponentChildren;
  nav: ComponentChildren;
  children: ComponentChildren;
  actions?: ComponentChildren;
  crumbs?: Crumb[];
  hideHeader?: boolean;
  accountMenu: ComponentChildren;
};

function MobileMenu(props: { nav: ComponentChildren; onClose(): void }) {
  useEsc(props.onClose, { respectDefault: true });
  return (
    <div
      class="absolute top-full inset-x-0 animate-slide-bottom bg-white p-2 py-6 flex flex-col shadow-xl z-20"
      onClick={(e) => e.preventDefault()}
    >
      {props.nav}
    </div>
  );
}

export function RootUserMenu(props: { renderMenu(): JSX.Element }) {
  const configuration = useConfiguration();
  const user = useCurrentUser()!;
  const isMimicking = !!configuration?.coreUser;

  async function stopMimickingUser() {
    try {
      await rpx.admin.unmimicUser();
      // Opens the homepage and hard reloads the app
      window.location.href = `/admin/people/${user?.id}`;
    } catch (err) {
      showError(err);
    }
  }

  return (
    <Dropdown
      triggerClass="max-w-full"
      hideDownIcon
      noPadding
      class="max-w-full"
      position={isMimicking ? 'top-4 right-0' : undefined}
      renderMenu={() => (
        <div class="p-2 gap-2 flex flex-col">
          {configuration?.coreUser && (
            <>
              <Button
                class="w-full flex gap-4 items-center p-4 text-left hover:bg-gray-100 hover:dark:bg-gray-700 focus:outline-hidden transition-all rounded-md text-gray-800 dark:text-gray-100"
                role="menuitem"
                onClick={stopMimickingUser}
              >
                <UserProfileIcon user={configuration.coreUser} size="size-8" />
                <div class="flex flex-col leading-snug">
                  <span class="opacity-75">Switch back to</span>
                  <span class="font-bold">{configuration.coreUser.name}</span>
                </div>
              </Button>
              <span class="border-t"></span>
            </>
          )}
          {!configuration?.coreUser && user && (
            <div class="flex items-center p-4 gap-4 text-left border-b dark:border-gray-700 text-gray-700 dark:text-gray-100">
              <UserProfileIcon user={user} size="size-8" />
              <div class="flex flex-col">
                <span class="font-bold">{user.name}</span>
              </div>
            </div>
          )}

          <div class="flex flex-col">{props.renderMenu()}</div>
        </div>
      )}
    >
      <span
        class={`${
          isMimicking
            ? 'bg-green-600 text-white p-1 sm:pl-4 ring-2 ring-green-200 rounded-full'
            : ''
        } inline-flex items-center gap-4`}
      >
        {isMimicking && (
          <span class="hidden sm:block font-semibold whitespace-nowrap">Mimicking {user.name}</span>
        )}
        <UserProfileIcon
          user={user}
          size="w-8 h-8 sm:w-8 sm:h-8 text-xs ring-2 ring-black/50 hover:ring-black"
        />
      </span>
    </Dropdown>
  );
}

export function RootNav(props: { children: ComponentChildren }) {
  const { isCollapsed } = useContext(SidebarContext);
  return (
    <nav class={`p-2 flex flex-col grow gap-3 ${isCollapsed ? 'items-center' : ''}`}>
      {props.children}
    </nav>
  );
}

export function RootNavlink(props: {
  href: string;
  isSelected?: boolean;
  Ico: typeof IcoCalendar;
  text: ComponentChildren;
}) {
  const { isCollapsed } = useContext(SidebarContext);
  return (
    <a
      href={props.href}
      class={`${
        props.isSelected ? 'text-indigo-800 bg-indigo-100' : 'text-inherit'
      } p-2 px-3 font-semibold rounded-md transition-all flex items-center gap-3 hover:text-indigo-700 hover:bg-indigo-100`}
      title={isCollapsed ? String(props.text) : undefined}
    >
      <props.Ico class="size-5 shrink-0 opacity-75" />
      {!isCollapsed && props.text}
    </a>
  );
}

export function RootPage(props: RootPageProps) {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useLocalStorageState(
    'sidebar-collapsed',
    false,
  );

  useDocumentTitle([props.title]);

  return (
    <SidebarContext.Provider value={{ isCollapsed: isSidebarCollapsed }}>
      <section class="relative isolate flex flex-col min-h-svh w-full bg-white">
        <header class="flex flex-col relative">
          {isMobileMenuOpen && (
            <MobileMenu nav={props.nav} onClose={() => setIsMobileMenuOpen(false)} />
          )}
          <nav class="lg:hidden p-3 border-b flex justify-between gap-4">
            {props.logo}
            {props.accountMenu}
          </nav>
          <nav class="lg:hidden flex items-center border-b relative z-20 font-bold text-gray-900">
            <Button
              class="inline-flex items-center gap-3 p-3"
              onClick={() => setIsMobileMenuOpen((x) => !x)}
            >
              <span class="size-6 relative">
                <IcoList
                  class={`size-6 absolute top-0 left-0 transition-all ${
                    isMobileMenuOpen ? 'opacity-0' : 'opacity-75'
                  }`}
                />
                <IcoX
                  class={`size-6 absolute top-0 left-0 transition-all ${
                    isMobileMenuOpen ? 'opacity-75' : 'opacity-0'
                  }`}
                />
              </span>
            </Button>
            <h1 class="font-normal">
              {props.crumbs && <Breadcrumbs crumbs={props.crumbs} />}
              {!props.crumbs && props.title}
            </h1>
          </nav>
        </header>

        <div class="flex grow">
          <div
            class={`hidden lg:flex flex-col py-4 bg-gray-50 border-r transition-all duration-300 ${
              isSidebarCollapsed ? 'w-16' : 'w-64'
            }`}
          >
            <header
              class={`mb-4 flex items-center ${isSidebarCollapsed ? 'justify-center' : 'px-4 justify-between'}`}
            >
              {!isSidebarCollapsed && props.logo}
              <Button
                class={`p-2 hover:bg-gray-200 rounded-md transition-colors ${!isSidebarCollapsed ? '-mr-3' : ''}`}
                onClick={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
                title={isSidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
              >
                <IcoList class="size-5" />
              </Button>
            </header>
            {props.nav}
          </div>
          <div class="flex flex-col grow rounded-lg">
            {!props.hideHeader && (
              <header class="hidden lg:flex items-center justify-between p-4 border-b gap-8">
                <h1 class="font-semibold">
                  {props.crumbs && <Breadcrumbs crumbs={props.crumbs} />}
                </h1>
                <div class="flex gap-8">
                  <nav class="flex gap-2">{props.actions}</nav>
                  {props.accountMenu}
                </div>
              </header>
            )}
            {props.children}
          </div>
        </div>
      </section>
    </SidebarContext.Provider>
  );
}
